import { useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useRouter } from '@tanstack/react-router'
import type { DriveStep } from 'driver.js'
import {
    useAppWalkthroughDriverConfig,
    registerStepMultiHighlight,
    registerStepInteractionMode,
    clearStepMultiHighlights,
    setWalkthroughStepInfo,
    getWalkthroughStartStep,
    ensureAppWalkthroughConfig,
    setOnExplicitDismissCallback,
} from './useAppWalkthroughDriverConfig'
import {
    getWalkthroughState,
    saveWalkthroughState,
} from '@/utils/appWalkthroughStorage'
import { WALKTHROUGH_STEPS, TOTAL_STEPS } from './walkthroughSteps'
import useAnalyticsStore from '@/features/Analytics/stores/useAnalyticsStore/useAnalyticsStore'
import useWalkthroughStore from '@/stores/useWalkthroughStore/useWalkthroughStore'

export const useAppWalkthrough = () => {
    const driver = useAppWalkthroughDriverConfig()
    const { t } = useTranslation('translations')
    const navigate = useNavigate()
    const router = useRouter()
    const setIsChartSummaryHovered = useAnalyticsStore(
        (state) => state.usageAndCost.setIsChartSummaryHovered
    )
    const setIsReportSettingsOpen = useAnalyticsStore(
        (state) => state.usageAndCost.setIsReportSettingsOpen
    )
    const setWalkthroughState = useWalkthroughStore(
        (state) => state.setWalkthroughState
    )
    const summaryButtonRef = useRef<HTMLElement | null>(null)
    const isActiveRef = useRef<boolean>(false)
    const isTogglingDropdownRef = useRef<boolean>(false)
    const dropdownObserverRef = useRef<MutationObserver | null>(null)
    const isExplicitDismissRef = useRef<boolean>(false)
    const originalMoveNextRef = useRef<(() => void) | null>(null)
    const originalDestroyRef = useRef<(() => void) | null>(null)

    useEffect(() => {
        const config = driver.getConfig()
        driver.setConfig({
            ...config,
            nextBtnText: t('app_walkthrough.next'),
            prevBtnText: t('app_walkthrough.previous'),
            doneBtnText: t('app_walkthrough.done'),
            closeBtnText: t('app_walkthrough.dismiss'),
        } as typeof config)
    }, [driver, t])

    const waitForElement = useCallback(
        async (
            selector: string,
            timeout = 5000
        ): Promise<HTMLElement | null> => {
            const startTime = Date.now()
            while (Date.now() - startTime < timeout) {
                const element = document.querySelector<HTMLElement>(selector)
                if (element) {
                    return element
                }
                await new Promise((resolve) => setTimeout(resolve, 100))
            }
            return null
        },
        []
    )

    const startWatchingDropdown = useCallback(() => {
        // Clean up existing observer
        if (dropdownObserverRef.current) {
            dropdownObserverRef.current.disconnect()
        }

        const dropdown = document.querySelector(
            '[data-testid="summary-dropdown-dropdown-content"]'
        )
        if (!dropdown) return

        // Watch for class changes on the dropdown
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (
                    mutation.type === 'attributes' &&
                    mutation.attributeName === 'class'
                ) {
                    const target = mutation.target as HTMLElement
                    if (!target.classList.contains('is-highlighted')) {
                        target.classList.add('is-highlighted')
                    }
                }
            })
        })

        observer.observe(dropdown, {
            attributes: true,
            attributeFilter: ['class'],
        })

        dropdownObserverRef.current = observer
    }, [])

    const stopWatchingDropdown = useCallback(() => {
        if (dropdownObserverRef.current) {
            dropdownObserverRef.current.disconnect()
            dropdownObserverRef.current = null
        }
    }, [])

    const handleSummaryDropdownToggle = useCallback(
        async (step: number, isActive: boolean) => {
            if (step === 6) {
                const summaryButton = document.querySelector<HTMLElement>(
                    '[data-walkthrough="summary-dropdown"]'
                )

                if (summaryButton) {
                    summaryButtonRef.current = summaryButton
                    if (isActive) {
                        // Prevent duplicate calls from toggling the dropdown
                        if (isTogglingDropdownRef.current) {
                            return
                        }
                        isTogglingDropdownRef.current = true

                        setIsChartSummaryHovered(true)
                        const dropdown = document.querySelector(
                            '[data-testid="summary-dropdown-dropdown-content"]'
                        )
                        const isCurrentlyOpen =
                            dropdown && !dropdown.classList.contains('hidden')

                        startWatchingDropdown()

                        if (!isCurrentlyOpen) {
                            await new Promise((resolve) =>
                                requestAnimationFrame(() => {
                                    resolve(null)
                                })
                            )
                            summaryButton.click()
                        }
                        isTogglingDropdownRef.current = false
                    } else {
                        isTogglingDropdownRef.current = false
                        setIsChartSummaryHovered(false)
                        stopWatchingDropdown()
                        const dropdown = document.querySelector(
                            '[data-testid="summary-dropdown-dropdown-content"]'
                        )
                        if (
                            dropdown &&
                            !dropdown.classList.contains('hidden')
                        ) {
                            summaryButton.click()
                        }
                    }
                }
            } else {
                if (summaryButtonRef.current) {
                    setIsChartSummaryHovered(false)
                    stopWatchingDropdown()
                    const dropdown = document.querySelector(
                        '[data-testid="summary-dropdown-dropdown-content"]'
                    )
                    if (dropdown && !dropdown.classList.contains('hidden')) {
                        summaryButtonRef.current.click()
                    }
                }
            }
        },
        [setIsChartSummaryHovered, startWatchingDropdown, stopWatchingDropdown]
    )

    const handleReportSettingsDrawerToggle = useCallback(
        (step: number, isActive: boolean) => {
            if (step === 4) {
                setIsReportSettingsOpen(isActive)
            } else {
                setIsReportSettingsOpen(false)
            }
        },
        [setIsReportSettingsOpen]
    )

    const navigateToStepRoute = useCallback(
        async (step: number) => {
            const stepConfig = WALKTHROUGH_STEPS.find((s) => s.step === step)
            if (!stepConfig) return

            // Use router.state.location.pathname for accurate, real-time route checking
            const currentPath = router.state.location.pathname
            if (currentPath !== stepConfig.route) {
                await navigate({ to: stepConfig.route })

                // Wait for the target route's primary element to appear, proving the page is ready
                const primarySelector = stepConfig.elementSelectors
                    ? stepConfig.elementSelectors[
                          stepConfig.elementSelectors.length - 1
                      ]
                    : stepConfig.elementSelector

                if (primarySelector) {
                    await waitForElement(primarySelector, 5000)
                }
            }
        },
        [navigate, router, waitForElement]
    )

    const buildDriverSteps = useCallback(
        async (startStep: number): Promise<DriveStep[]> => {
            const steps: DriveStep[] = []

            await navigateToStepRoute(startStep)

            const currentRoute = WALKTHROUGH_STEPS[startStep - 1]?.route

            for (let i = startStep; i <= TOTAL_STEPS; i++) {
                const stepConfig = WALKTHROUGH_STEPS[i - 1]

                if (i > startStep && stepConfig.route !== currentRoute) {
                    break
                }

                const isMultiElement = !!stepConfig.elementSelectors
                const primarySelector: string | undefined = isMultiElement
                    ? stepConfig.elementSelectors?.[
                          stepConfig.elementSelectors.length - 1
                      ]
                    : stepConfig.elementSelector

                const primaryElement = await waitForElement(
                    primarySelector || ''
                )

                if (primaryElement) {
                    if (isMultiElement && stepConfig.elementSelectors) {
                        const allElements = await Promise.all(
                            stepConfig.elementSelectors.map((sel) =>
                                waitForElement(sel)
                            )
                        )

                        if (allElements.every((el) => el !== null)) {
                            const siblingSelectors =
                                stepConfig.elementSelectors.filter(
                                    (sel) => sel !== primarySelector
                                )
                            registerStepMultiHighlight(
                                steps.length,
                                siblingSelectors
                            )
                        }
                    }

                    registerStepInteractionMode(
                        steps.length,
                        stepConfig.allowInteraction === true
                    )

                    const step: DriveStep = {
                        element: primaryElement,
                        popover: {
                            title: t(stepConfig.titleKey),
                            description: t(stepConfig.descriptionKey),
                            side: stepConfig.popoverSide ?? 'bottom',
                            align: stepConfig.popoverAlign ?? 'center',
                            showButtons: ['next', 'close'] as Array<
                                'next' | 'previous' | 'close'
                            >,
                            nextBtnText:
                                i === TOTAL_STEPS
                                    ? t('app_walkthrough.done')
                                    : undefined,
                        },
                    }

                    steps.push(step)
                } else {
                    break
                }
            }

            return steps
        },
        [t, navigateToStepRoute, waitForElement]
    )

    const startWalkthrough = useCallback(
        async (fromStep: number = 1) => {
            if (isActiveRef.current) {
                return
            }

            const state = getWalkthroughState()
            if (state.dismissed || state.completed) {
                return
            }

            clearStepMultiHighlights()

            const startStep = fromStep || state.currentStep || 1
            isActiveRef.current = true

            setWalkthroughState(true, startStep)

            setWalkthroughStepInfo(startStep, TOTAL_STEPS)

            ensureAppWalkthroughConfig(driver)

            // Set up explicit dismiss callback for the footer close button
            setOnExplicitDismissCallback(() => {
                isExplicitDismissRef.current = true
                driver.destroy()
            })

            try {
                await navigateToStepRoute(startStep)

                const steps = await buildDriverSteps(startStep)

                if (steps.length === 0) {
                    isActiveRef.current = false
                    return
                }

                driver.destroy()

                driver.setSteps(steps)

                // Store the REAL original functions only once
                if (!originalMoveNextRef.current) {
                    originalMoveNextRef.current = driver.moveNext.bind(driver)
                }
                if (!originalDestroyRef.current) {
                    originalDestroyRef.current = driver.destroy.bind(driver)
                }

                const originalMoveNext = originalMoveNextRef.current
                const originalDestroy = originalDestroyRef.current

                driver.moveNext = () => {
                    const driverIndex = driver.getActiveIndex() ?? 0
                    const walkthroughStartStep = getWalkthroughStartStep()
                    const currentStep = walkthroughStartStep + driverIndex
                    const currentStepConfig = WALKTHROUGH_STEPS[currentStep - 1]

                    void handleSummaryDropdownToggle(currentStep, false)
                    handleReportSettingsDrawerToggle(currentStep, false)

                    // Clear hover state when leaving step 6
                    if (currentStep === 6) {
                        setIsChartSummaryHovered(false)
                        stopWatchingDropdown()
                    }

                    const currentState = getWalkthroughState()
                    if (currentStep < TOTAL_STEPS) {
                        const nextStep = currentStep + 1
                        const nextStepConfig = WALKTHROUGH_STEPS[nextStep - 1]

                        const needsRouteChange =
                            currentStepConfig.route !== nextStepConfig.route

                        if (needsRouteChange) {
                            saveWalkthroughState({
                                ...currentState,
                                currentStep: nextStep,
                            })

                            setWalkthroughState(true, nextStep)

                            setWalkthroughStepInfo(nextStep, TOTAL_STEPS)

                            navigateToStepRoute(nextStep)
                                .then(() => {
                                    originalDestroy()
                                    isActiveRef.current = false

                                    void startWalkthrough(nextStep)
                                })
                                .catch(() => {
                                    originalDestroy()
                                    isActiveRef.current = false
                                })
                        } else {
                            saveWalkthroughState({
                                ...currentState,
                                currentStep: nextStep,
                            })

                            setWalkthroughState(true, nextStep)

                            if (nextStep === 4) {
                                handleReportSettingsDrawerToggle(4, true)
                                // Wait for drawer animation to complete before showing popover
                                setTimeout(() => {
                                    originalMoveNext()
                                }, 100)
                            } else if (nextStep === 6) {
                                // Wait for dropdown to open before progressing
                                void handleSummaryDropdownToggle(6, true).then(
                                    () => {
                                        originalMoveNext()
                                    }
                                )
                            } else {
                                originalMoveNext()
                            }
                        }
                    } else {
                        saveWalkthroughState({
                            ...currentState,
                            completed: true,
                            currentStep: TOTAL_STEPS,
                        })
                        isActiveRef.current = false

                        setWalkthroughState(false, null)
                        setOnExplicitDismissCallback(null)

                        originalMoveNext()
                    }
                }

                driver.destroy = () => {
                    const driverIndex = driver.getActiveIndex() ?? 0
                    const walkthroughStartStep = getWalkthroughStartStep()
                    const currentStep = walkthroughStartStep + driverIndex
                    const currentState = getWalkthroughState()
                    void handleSummaryDropdownToggle(currentStep, false)
                    handleReportSettingsDrawerToggle(currentStep, false)
                    // Only save dismissed state if user explicitly dismissed (not during cleanup)
                    if (
                        isExplicitDismissRef.current &&
                        !currentState.completed
                    ) {
                        saveWalkthroughState({
                            ...currentState,
                            dismissed: true,
                        })
                    }
                    isActiveRef.current = false
                    isExplicitDismissRef.current = false

                    setWalkthroughState(false, null)

                    originalDestroy()
                }

                if (startStep === 4) {
                    handleReportSettingsDrawerToggle(4, true)
                    // Wait for drawer animation to complete before starting driver
                    setTimeout(() => {
                        driver.drive()
                    }, 100)
                } else if (startStep === 6) {
                    void handleSummaryDropdownToggle(6, true).then(() => {
                        driver.drive()
                    })
                } else {
                    driver.drive()
                }
            } catch {
                isActiveRef.current = false
                setWalkthroughState(false, null)
            }
        },
        [
            driver,
            navigateToStepRoute,
            buildDriverSteps,
            handleSummaryDropdownToggle,
            handleReportSettingsDrawerToggle,
            setWalkthroughState,
            setIsChartSummaryHovered,
            stopWatchingDropdown,
        ]
    )

    const dismissWalkthrough = useCallback(() => {
        isExplicitDismissRef.current = true
        driver.destroy()
    }, [driver])

    useEffect(() => {
        return () => {
            if (isActiveRef.current) {
                const driverIndex = driver.getActiveIndex() ?? 0
                const walkthroughStartStep = getWalkthroughStartStep()
                const currentStep = walkthroughStartStep + driverIndex
                void handleSummaryDropdownToggle(currentStep, false)
                handleReportSettingsDrawerToggle(currentStep, false)
                driver.destroy()
                isActiveRef.current = false
            }
            stopWatchingDropdown()
            setOnExplicitDismissCallback(null)
        }
    }, [
        driver,
        handleSummaryDropdownToggle,
        handleReportSettingsDrawerToggle,
        stopWatchingDropdown,
    ])

    return {
        driver,
        startWalkthrough,
        dismissWalkthrough,
        isActive: isActiveRef.current,
    }
}
